import { PhoneOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './on-hold-indicator.module.scss';

interface OnHoldIndicatorProps {
  // ISO timestamp of when the caller went on hold. Used both for the
  // visible wait-time pill (so reps can prioritize older holds at a
  // glance) and the tooltip with a precise start time.
  since?: string | null;
}

// Format elapsed milliseconds as a compact label: 0:45 / 1:12 / 12:03.
// Cap-and-pad to mm:ss; we deliberately don't switch to hours, because a
// queued call older than 99 minutes signals a service-level problem and
// should keep counting in minutes so it draws the rep's attention.
function formatElapsed(sinceIso: string): string {
  const ms = Math.max(0, Date.now() - new Date(sinceIso).getTime());
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Compact badge shown on queued AnalyticsCall session cards in Command
 * Center to indicate the caller is currently hearing hold music while
 * waiting for a rep to claim. Three animated dots after the phone icon
 * give it a passive-pulse rhythm so the rep notices it across the room.
 * The live mm:ss timer lets the rep prioritize the longest-waiting hold
 * without hovering — combined with SessionList's created_at-asc sort on
 * the queued group, the oldest hold visually surfaces at the top.
 *
 * Only relevant for AnalyticsCall in webchat_status='queued' — chat /
 * SMS / email queues don't have a "hold music" concept, so the parent
 * (session-card.tsx) gates rendering on type + status.
 */
export const OnHoldIndicator = ({ since }: OnHoldIndicatorProps) => {
  const { t } = useTranslation('webchat');
  const [elapsed, setElapsed] = useState(() => (since ? formatElapsed(since) : ''));

  // Tick once per second. Guard on `since` so unmounted/missing-timestamp
  // cards don't spin a wasted interval.
  useEffect(() => {
    if (!since) return undefined;
    setElapsed(formatElapsed(since));
    const timer = setInterval(() => setElapsed(formatElapsed(since)), 1000);
    return () => clearInterval(timer);
  }, [since]);

  const tooltip = since
    ? t('onHold.tooltipSince', { time: dayjs(since).format('HH:mm'), ago: dayjs(since).fromNow() })
    : t('onHold.tooltipNoSince');

  return (
    <Tooltip title={tooltip} placement="top">
      <span
        className={styles.indicator}
        role="status"
        aria-label={t('onHold.ariaLabel')}
        data-testid="on-hold-indicator"
      >
        <PhoneOutlined className={styles.icon} />
        <span className={styles.label}>{t('onHold.label')}</span>
        {since && <span className={styles.timer} data-testid="on-hold-timer">{elapsed}</span>}
        <span className={styles.pulse} aria-hidden="true">
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </span>
      </span>
    </Tooltip>
  );
};
